import type {DashCardConfig} from '@/config/menuConfig';
import {userService} from '@/services/userService';
import type {
  AdminAfriexPerson,
  AdminPaginatedEnvelope,
  AfriexAdmin,
  AfriexAdminTransaction,
  AfriexLocationInfo,
  RatesMap,
  SupportedAssets,
} from '@/types';
import {
  AdminTransactionTypes,
  AfriexTransactionChannel,
  AfriexTransactionType,
} from '@/types';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {BusinessTransactionChannel} from '@/types/AfriexBusinessTransactionChannel';
import type {BusinessTransactionType} from '@/types/AfriexBusinessTransactionType';

import {capitalizeWords} from './strings';

// Re-export so existing `from '@/utils/dashboard'` callers keep working; new
// callers should reach `./strings` directly. Moved out of this file to let
// `types/AfriexFees.ts` depend on a leaf util instead of pulling in
// dashboard.ts's whole transitive graph (which created a types -> utils ->
// config -> types cycle).
export {capitalizeWords};

export function cleanText(input: string): string {
  return input.replace(/\*/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Formats an admin transaction by adding the `ADMIN` prefix to the transaction type
 * and fetching the admin user's name and email.
 * @param data The transaction object to format.
 * @returns An object containing the formatted transaction type and admin user information.
 */
export const formatAdminTransaction = async (data: AfriexAdminTransaction) => {
  let {type} = data;
  let adminUser: {name: string | undefined; email: string | undefined} | null =
    null;

  if (data.channel === AfriexTransactionChannel.ADMIN) {
    type = `ADMIN_${data.type}` as AfriexTransactionType;
    try {
      const getAdminUser = data.initiatedBy
        ? await userService.getUser(data.initiatedBy)
        : undefined;
      adminUser = {
        name: getAdminUser?.name?.fullName,
        email: getAdminUser?.currentEmail,
      };
    } catch (error) {
      console.error('Error fetching admin user:', error);
    }
  }

  return {
    type,
    adminUser: adminUser ?? null,
  };
};

/**
 * Formats an admin business transaction by adding the `ADMIN` prefix to the transaction type
 * and fetching the admin user's name and email.
 * @param data The transaction object to format.
 * @returns An object containing the formatted transaction type and admin user information.
 */
export const formatBusinessAdminTransaction = async (
  data: AfriexBusinessTransaction,
) => {
  let {type} = data;
  let adminUser: {name: string | undefined; email: string | undefined} | null =
    null;

  if (data.sourcePaymentMethod.channel === BusinessTransactionChannel.ADMIN) {
    type = `ADMIN_${data.type}` as BusinessTransactionType;
    try {
      const getAdminUser = data.sourcePaymentMethod.userId
        ? await userService.getUser(data.sourcePaymentMethod.userId)
        : undefined;
      adminUser = {
        name: getAdminUser?.name?.fullName,
        email: getAdminUser?.currentEmail,
      };
    } catch (error) {
      console.error('Error fetching admin user:', error);
    }
  }

  return {
    type,
    adminUser: adminUser ?? null,
  };
};

/**
 * Formats an admin transaction by adding the `ADMIN` prefix to the transaction type
 * and fetching the admin user's name and email.
 * @param data The transaction object to format.
 * @returns An object containing the formatted transaction type and admin user information.
 */
export const formatAdminBusinessTransaction = async (
  data: AfriexBusinessTransaction,
) => {
  let {type} = data;
  let adminUser: {name: string | undefined; email: string | undefined} | null =
    null;

  if (data.sourcePaymentMethod.channel === BusinessTransactionChannel.ADMIN) {
    type = `ADMIN_${data.type}` as BusinessTransactionType;
    try {
      const getAdminUser = data?.sourcePaymentMethod?.userId
        ? await userService.getUser(data.sourcePaymentMethod.userId)
        : undefined;
      adminUser = {
        name: getAdminUser?.name?.fullName,
        email: getAdminUser?.currentEmail,
      };
    } catch (error) {
      console.error('Error fetching admin user:', error);
    }
  }

  return {
    type,
    adminUser: adminUser ?? null,
  };
};

/**
 * Formats the transaction channel by capitalizing the channel name and adding "(E2E)" if the transaction is E2E.
 * @param tx The transaction object to format the channel for.
 * @returns The formatted transaction channel as a string.
 */
export const formatTxChannel = (tx: AfriexAdminTransaction) => {
  const hasE2e = tx.processorResponseMeta?.transactionType === 'E2E';
  const rewardTxHasE2E = tx?.meta?.transactionType === 'E2E';
  return `${capitalizeWords(tx.channel?.replace(/_/g, ' '))} ${hasE2e || rewardTxHasE2E ? '(E2E)' : ''}`;
};

/**
 * Calculates and returns the exchange rate for a transaction.
 * @param transaction The transaction object to calculate the exchange rate for.
 * @returns The exchange rate as a string.
 */
export const showCurrencyRate = (
  transaction: AfriexAdminTransaction,
): string => {
  const {isAdmin, fulfilledAsset} = getTxAmountAndAsset(transaction);
  const ratesObject = transaction?.rates as RatesMap;
  const sourceAsset = transaction.sourceAsset as SupportedAssets;
  const targetAsset = isAdmin
    ? sourceAsset
    : (fulfilledAsset as SupportedAssets);

  const nestedRate = ratesObject?.[sourceAsset]?.[targetAsset] ?? 0;
  const flattenedRate = ratesObject?.[targetAsset] ?? 0;

  const exchangeRate =
    flattenedRate?.constructor === Object ? nestedRate : flattenedRate;

  return `1 ${sourceAsset} = ${formatValues(Number(exchangeRate), 'rate', 6)} ${targetAsset}`;
};

/**
 * Compares two display fields and returns an array containing either just the preferred field
 * (if values match) or both fields (if values differ).
 *
 * @param preferredField - The preferred field object with key-value pair
 * @param secondaryField - The secondary field object with key-value pair
 * @returns Array of field objects to display
 */
export const compareAndFilterFields = <
  Preferred extends Record<string, unknown>,
  Secondary extends Record<string, unknown>,
>(
  preferredField: Preferred,
  secondaryField: Secondary,
): Array<Preferred | Secondary> => {
  // Extract keys and values. The raw values go through locals so `typeof`
  // narrows them; an element access keyed by a non-literal string does not.
  const preferredKey = Object.keys(preferredField)[0];
  const secondaryKey = Object.keys(secondaryField)[0];
  const preferredRaw = preferredField[preferredKey];
  const secondaryRaw = secondaryField[secondaryKey];
  const preferredValue =
    typeof preferredRaw === 'string'
      ? capitalizeWords(preferredRaw.replace(/_/g, ' '))
      : preferredRaw;
  const secondaryValue =
    typeof secondaryRaw === 'string'
      ? capitalizeWords(secondaryRaw.replace(/_/g, ' '))
      : secondaryRaw;

  // If values are exactly the same, return only the preferred field
  if (preferredValue === secondaryValue) {
    return [preferredField];
  }

  // If values are different, return both fields
  return [preferredField, secondaryField];
};

/**
 * Generates the amount fields for a transaction.
 * @param transaction The transaction object to generate the amount fields for.
 * @returns An array of objects containing the amount fields.
 */
export const generateTxAmountFields = (transaction: AfriexAdminTransaction) => {
  const {initiatedAmount, initiatedAsset, fulfilledAmount, fulfilledAsset} =
    getTxAmountAndAsset(transaction);

  const initiatedAmountField = {
    'Initiated Amount': `${initiatedAmount} ${initiatedAsset}`,
  };

  const fulfilledAmountField = {
    'Fulfilled Amount': `${fulfilledAmount} ${fulfilledAsset}`,
  };

  return compareAndFilterFields(initiatedAmountField, fulfilledAmountField);
};

/**
 * Generates the amount fields for a transaction.
 * @param transaction The transaction object to generate the amount fields for.
 * @returns An array of objects containing the amount fields.
 */
export const generateBusinessTxAmountFields = (
  transaction: AfriexBusinessTransaction,
) => {
  const {initiatedAmount, initiatedAsset, fulfilledAmount, fulfilledAsset} =
    getBusinessTxAmountAndAsset(transaction);

  const initiatedAmountField = {
    'Initiated Amount': `${initiatedAmount} ${initiatedAsset}`,
  };

  const fulfilledAmountField = {
    'Fulfilled Amount': `${fulfilledAmount} ${fulfilledAsset}`,
  };

  return compareAndFilterFields(initiatedAmountField, fulfilledAmountField);
};

/**
 * Formats a value based on the specified type and options.
 * @param value The numeric value to format.
 * @param type The type of formatting to apply ("money" or "rate").
 * @param fraction The number of decimal places to display (optional).
 * @param showCurrencySymbol Whether to display the currency symbol (optional).
 * @returns The formatted value as a string.
 */
export const formatValues = (
  value: number,
  type: DashCardConfig['valueType'],
  fraction?: number, // Control decimal places
  showCurrencySymbol = false, // Control currency symbol display
) => {
  let formattedValue: string;
  switch (type) {
    case 'money':
      formattedValue = new Intl.NumberFormat('en-US', {
        ...(showCurrencySymbol && {
          currencyDisplay: 'symbol',
          style: 'currency',
          currency: 'USD',
        }),
        maximumFractionDigits: fraction !== undefined ? fraction : 2,
        minimumFractionDigits: fraction !== undefined ? fraction : 2,
      }).format(value);
      break;

    case 'rate': {
      const rateDigits = fraction !== undefined ? fraction : 2;
      formattedValue = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: rateDigits,
        minimumFractionDigits: Number.isInteger(value) ? 0 : rateDigits,
      }).format(value);
      break;
    }

    default:
      formattedValue = '0'; // Fallback for unexpected types
  }

  // Trim trailing zeros if the value is a decimal
  if (formattedValue.includes('.')) {
    formattedValue = formattedValue.replace(/\.?0+$/, '');
  }

  return formattedValue;
};

/**
 * Returns the transaction type for all transactions taking into account
 * the admin transaction types.
 * @param transaction The transaction object to get the type for.
 * @returns The transaction type for an admin transaction.
 */
export const getTxTypeCheck = (transaction: AfriexAdminTransaction) =>
  Object.values(AdminTransactionTypes).find(
    (key) =>
      key ===
      AdminTransactionTypes[
        transaction.type as unknown as keyof typeof AdminTransactionTypes
      ],
  ) || transaction.type;

/**
 * Returns the transaction type for all business transactions taking into account
 * the admin transaction types.
 * @param transaction The business transaction object to get the type for.
 * @returns The business transaction type for an admin transaction.
 */
export const getBusinessTxTypeCheck = (
  transaction: AfriexBusinessTransaction,
): BusinessTransactionType | string | undefined => {
  const {type} = transaction;
  for (const [adminKey, baseType] of Object.entries(AdminTransactionTypes)) {
    if (adminKey === type) {
      return baseType;
    }
  }
  return type;
};

/**
 * Returns the transaction amount and asset for a transaction.
 * @param transaction The transaction object to get the amount and asset for.
 * @param type The type of formatting to apply ("money" or "rate").
 * @returns { isAdmin, initiatedAmount, initiatedAsset, fulfilledAmount, fulfilledAsset, txType }
 */
export const getTxAmountAndAsset = (
  transaction: AfriexAdminTransaction,
  type = 'money' as DashCardConfig['valueType'],
) => {
  type AmountType = string | number | undefined;
  const txType = getTxTypeCheck(transaction);
  const isAdmin = transaction.channel === AfriexTransactionChannel.ADMIN;

  // Initialize with default values
  let initiatedAmount: AmountType = transaction.sourceAmount;
  const initiatedAsset = transaction.sourceAsset;
  let fulfilledAmount: AmountType, fulfilledAsset: SupportedAssets | undefined;

  // Set fulfilled values based on transaction type and channel
  switch (txType) {
    case AfriexTransactionType.WITHDRAW:
    case AfriexTransactionType.DEPOSIT:
    case AfriexTransactionType.REFERRAL:
    case AfriexTransactionType.BONUS:
      if (isAdmin) {
        fulfilledAmount = transaction.sourceAmount;
        fulfilledAsset = transaction.sourceAsset;
      } else {
        fulfilledAmount = transaction.destinationAmount;
        fulfilledAsset = transaction.destinationAsset;
      }
      break;
    case AfriexTransactionType.REVERSAL:
    case AfriexTransactionType.TRANSFER:
    case AfriexTransactionType.SWAP:
    default:
      fulfilledAmount = transaction.destinationAmount;
      fulfilledAsset = transaction.destinationAsset;
      break;
  }

  // Format amounts
  initiatedAmount = formatValues(Number(initiatedAmount), type);
  fulfilledAmount = formatValues(Number(fulfilledAmount), type);

  return {
    isAdmin,
    initiatedAmount,
    initiatedAsset,
    fulfilledAmount,
    fulfilledAsset,
    txType,
  };
};

/**
 * Returns the business transaction amount and asset for a transaction.
 * @param transaction The Business transaction object to get the amount and asset for.
 * @param type The type of formatting to apply ("money" or "rate").
 * @returns { isAdmin, initiatedAmount, initiatedAsset, fulfilledAmount, fulfilledAsset, txType }
 */
export const getBusinessTxAmountAndAsset = (
  transaction: AfriexBusinessTransaction,
  type = 'money' as DashCardConfig['valueType'],
) => {
  type AmountType = string | number | undefined;
  const txType = getBusinessTxTypeCheck(transaction);
  const isAdmin =
    transaction.sourcePaymentMethod.channel ===
    BusinessTransactionChannel.ADMIN;

  // Initialize with default values
  let initiatedAmount: AmountType = transaction.sourceAmount.value;
  const initiatedAsset = transaction.sourceAmount.currencyCode;
  let fulfilledAmount: AmountType, fulfilledAsset: SupportedAssets | undefined;

  // Set fulfilled values based on transaction type and channel
  switch (txType) {
    case AfriexTransactionType.WITHDRAW:
    case AfriexTransactionType.DEPOSIT:
    case AfriexTransactionType.REFERRAL:
    case AfriexTransactionType.BONUS:
      if (isAdmin) {
        fulfilledAmount = transaction.sourceAmount.value;
        fulfilledAsset = transaction.sourceAmount.currencyCode;
      } else {
        fulfilledAmount = transaction.destinationAmount.value;
        fulfilledAsset = transaction.destinationAmount.currencyCode;
      }
      break;
    case AfriexTransactionType.REVERSAL:
    case AfriexTransactionType.TRANSFER:
    case AfriexTransactionType.SWAP:
    default:
      fulfilledAmount = transaction.destinationAmount.value;
      fulfilledAsset = transaction.destinationAmount.currencyCode;
      break;
  }

  // Format amounts
  initiatedAmount = formatValues(Number(initiatedAmount), type);
  fulfilledAmount = formatValues(Number(fulfilledAmount), type);

  return {
    isAdmin,
    initiatedAmount,
    initiatedAsset,
    fulfilledAmount,
    fulfilledAsset,
    txType,
  };
};

/**
 * Modifies the admin profiles by adding the full name of the admin user.
 * @param adminProfiles The admin profiles to modify.
 * @param users The users to use for matching admin users.
 * @returns The modified admin profiles.
 */
export const modifyAdminProfiles = (
  adminProfiles: AdminPaginatedEnvelope<AfriexAdmin[]>[],
  users: AdminAfriexPerson[],
): AfriexAdmin[] => {
  const usersMap = new Map<string, string>();
  users.forEach((user) => {
    usersMap.set(user.id, user.name.fullName);
  });

  const modifiedAdmins: AfriexAdmin[] = adminProfiles
    .flatMap((page) => page.data)
    .map((admin) => ({
      ...admin,
      fullName: usersMap.get(admin.userId) || 'N/A',
    }));

  return modifiedAdmins;
};

/**
 * Returns the earliest location from an array of locations.
 * @param locations The array of locations to search.
 * @returns The earliest location or null if the array is empty.
 */
export const getEarliestLocation = (locations: AfriexLocationInfo[]) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return null;
  }

  return locations.reduce((earliest, current) => {
    const earliestDate = new Date(earliest.timestamp);
    const currentDate = new Date(current.timestamp);
    return currentDate < earliestDate ? current : earliest;
  });
};

export const snakeToSentenceCase = (snakeStr: string) => {
  if (!snakeStr) {
    return '';
  }
  const words = snakeStr?.toLowerCase().split('_');

  // Capitalize the first letter of the first word
  words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);

  // Join the words back into a single string with spaces
  return words?.join(' ')?.trim() ?? '';
};

export const isValidPositiveNumber = (raw: number | string) => {
  const value = Number(raw);
  // Check if the value is not null, undefined, or NaN
  if (value === null || value === undefined || Number.isNaN(value)) {
    return false;
  }

  // Check if the value is a finite number
  if (!Number.isFinite(value)) {
    return false;
  }

  if (value < 0) {
    return false;
  }

  // Check if the value is within the range of Number.MIN_SAFE_INTEGER and Number.MAX_SAFE_INTEGER
  if (value < Number.MIN_SAFE_INTEGER || value > Number.MAX_SAFE_INTEGER) {
    return false;
  }

  // If the value is a string, try to parse it as a number
  if (typeof value === 'string' && Number.isNaN(+value)) {
    return false;
  }

  // If none of the above conditions are met, the value is a valid number
  return true;
};

export const isolateErrorSync = <T>(
  functionToRun: () => T,
): [T | undefined, unknown] => {
  try {
    const result = functionToRun();
    return [result, undefined];
  } catch (err) {
    return [undefined, err];
  }
};

/**
 * Calculates and returns the exchange rate for a transaction.
 * @param transaction The transaction object to calculate the exchange rate for.
 * @returns The exchange rate as a string.
 */
export const showBusinessCurrencyRate = (
  transaction: AfriexBusinessTransaction,
): string => {
  const {isAdmin, fulfilledAsset} = getBusinessTxAmountAndAsset(transaction);
  const ratesObject = transaction?.meta?.rateMeta?.rates as RatesMap;
  const sourceAsset = transaction.sourceAmount?.currencyCode;
  const targetAsset = isAdmin ? sourceAsset : fulfilledAsset;

  const nestedRate = ratesObject?.[sourceAsset]?.[targetAsset] ?? 0;
  const flattenedRate = ratesObject?.[targetAsset] ?? 0;

  const exchangeRate =
    flattenedRate?.constructor === Object ? nestedRate : flattenedRate;

  if (!exchangeRate) {
    return '';
  }

  return `1 ${sourceAsset} = ${formatValues(Number(exchangeRate), 'rate', 6)} ${targetAsset}`;
};
